import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { JdsTag } from '../../../components/jds/JdsTag';
import { JdsButton } from '../../../components/jds/JdsButton';
import { CloseIcon, EyeIcon } from '../../../components/jds/icons';
import {
  BENEFIT_GROUPS,
  formatCurrency,
  formatCycle,
  getMonthlyEquivalent,
} from '../utils';

// Tom neutro (mesmo cinza do thead-light) pra marcar linhas que divergem
// entre os planos comparados. Nada de amber/warning aqui: variação entre
// planos não é um erro, então a cor não deve soar como alerta.
const DIFF_BG = 'var(--bg-neutral-layer02)';
const DIFF_BORDER = 'var(--border-neutral-strong)';

const stickyColumnStyle = {
  position: 'sticky',
  left: 0,
  background: 'var(--bg-neutral-layer01)',
  color: 'var(--fg-neutral-default)',
  fontSize: 'var(--fs-14)',
  fontWeight: 'var(--fw-regular)',
  zIndex: 1,
  minWidth: 220,
  maxWidth: 320,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const stickyDiffColumnStyle = {
  ...stickyColumnStyle,
  background: DIFF_BG,
  borderLeft: `var(--stroke-medium) solid ${DIFF_BORDER}`,
};

const stickyHeadStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  background: 'var(--bg-neutral-layer01)',
};

export function PlanComparisonTable({ plans, rows, onRemovePlan }) {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const visibleRows = onlyDiff ? rows.filter((row) => row.diff) : rows;
  // Cabeçalho de grupo (Créditos/Acessos) antes da primeira linha de cada tipo.
  const groupLabel = (row, index) => {
    if (index > 0 && visibleRows[index - 1].type === row.type) return null;
    return (
      BENEFIT_GROUPS.find((group) => group.type === row.type)?.label ?? 'Outros'
    );
  };

  return (
    <div className="jds-card mt-2">
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-4">
        <h2 className="jds-title-md mb-0">Comparativo de planos</h2>
        {plans.length > 1 && (
          <label
            className="d-flex align-items-center mb-0 text-muted"
            style={{ gap: 'var(--space-8)', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={onlyDiff}
              onChange={(e) => setOnlyDiff(e.target.checked)}
            />
            Mostrar só o que difere
          </label>
        )}
      </div>
      <div className="table-responsive" style={{ maxHeight: '70vh' }}>
        {plans.length === 0 ? (
          <p className="text-muted mb-0">
            Marque "Incluir no comparativo" em ao menos um plano acima.
          </p>
        ) : (
          <table className="table jds-table mb-0">
            <caption className="sr-only">
              Comparativo de itens incluídos em cada plano
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  style={{
                    ...stickyColumnStyle,
                    ...stickyHeadStyle,
                    zIndex: 3,
                    left: 0,
                    verticalAlign: 'bottom',
                  }}
                >
                  Item
                </th>
                {plans.map((plan) => (
                  <th
                    scope="col"
                    key={plan.id}
                    className="text-nowrap"
                    style={stickyHeadStyle}
                  >
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{ gap: 'var(--space-8)' }}
                    >
                      {plan.name}
                      <JdsButton
                        variant="ghost"
                        color="neutral"
                        size="sm"
                        iconOnly
                        title="Remover do comparativo"
                        aria-label={`Remover ${plan.name} do comparativo`}
                        onClick={() => onRemovePlan(plan.id)}
                      >
                        <CloseIcon />
                      </JdsButton>
                    </div>
                    <div style={{ fontWeight: 'var(--fw-regular)' }}>
                      <span
                        style={{
                          color: 'var(--fg-brand-strong)',
                          fontWeight: 'var(--fw-semibold)',
                        }}
                      >
                        {formatCurrency(plan.amount)}
                      </span>{' '}
                      <span className="text-muted">
                        · {formatCycle(plan.cycleDays)}
                      </span>
                    </div>
                    {getMonthlyEquivalent(plan) != null && (
                      <small className="text-muted">
                        ≈ {formatCurrency(getMonthlyEquivalent(plan))} / mês
                      </small>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <React.Fragment key={row.code}>
                  {groupLabel(row, index) && (
                    <tr>
                      <th
                        scope="rowgroup"
                        colSpan={plans.length + 1}
                        className="jds-title-xs text-muted"
                      >
                        {groupLabel(row, index)}
                      </th>
                    </tr>
                  )}
                  <tr
                    style={row.diff ? { backgroundColor: DIFF_BG } : undefined}
                  >
                    <th
                      scope="row"
                      style={
                        row.diff ? stickyDiffColumnStyle : stickyColumnStyle
                      }
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: 'var(--space-8)' }}
                      >
                        <Link
                          to={`/payments/items/${row.id}`}
                          className="text-muted"
                          aria-label={`Ver detalhes de ${row.name}`}
                          title="Ver detalhes"
                        >
                          <EyeIcon />
                        </Link>
                        <span>{row.name}</span>
                        {row.diff && <JdsTag label="Difere" />}
                      </div>
                    </th>
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        className={row.values[plan.id] ? '' : 'text-muted'}
                      >
                        {row.values[plan.id] || '—'}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
              {onlyDiff && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={plans.length + 1} className="text-muted">
                    Os planos selecionados incluem os mesmos produtos, com os
                    mesmos valores.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
